import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@contexts/AuthContext";
import { useCommunity } from "contexts/community";
import useModalZIndex from "@hooks/useModalZIndex";
import {
    COMMENT_MAX_LENGTH,
    commentPost,
    communitySlugFor,
    fetchComments,
    fetchPost,
    heartPost,
    postShareLink,
    postStore,
    withCommentCount,
} from "@helpers/posts";

const COMMENTS_PER_PAGE = 20;

const EMPTY_COMMENTS = {
    items: [],
    total: 0,
    page: 0,
    lastPage: 1,
    loading: false,
    error: false,
};

export default function usePostModal({ postId, open, onClose }) {
    const { t } = useTranslation("groupModals");
    const { user } = useAuth();
    const { community } = useCommunity();
    const { zIndex, isTop } = useModalZIndex(open);

    // The store's copy (when the feed already holds the post) shows at once;
    // the API refresh that follows replaces it through the subscription.
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const [comments, setComments] = useState(EMPTY_COMMENTS);
    const [sending, setSending] = useState(false);
    const [reacting, setReacting] = useState(false);

    const adminModalRef = useRef();
    const composerRef = useRef(null);
    const commentsRequest = useRef(0);

    // ── Post ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open || !postId) {
            setPost(null);
            setNotFound(false);
            return undefined;
        }

        let cancelled = false;
        setPost(postStore.get(postId) || null);
        setNotFound(false);

        const unsubscribe = postStore.subscribe((updated) => {
            if (updated.id !== postId) return;
            if (updated.removed) onClose();
            else setPost(updated);
        });

        fetchPost(postId).then((fresh) => {
            if (cancelled) return;
            if (fresh) postStore.set(fresh);
            else if (!postStore.get(postId)) setNotFound(true);
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, postId]);

    // ── Comments ──────────────────────────────────────────────────────────
    async function loadComments(page) {
        const request = ++commentsRequest.current;
        setComments((c) => ({ ...c, loading: true, error: false }));

        const res = await fetchComments(postId, page, COMMENTS_PER_PAGE);
        if (request !== commentsRequest.current) return;

        if (!res) {
            setComments((c) => ({ ...c, loading: false, error: true }));
            return;
        }

        setComments((c) => ({
            items: page === 1 ? res.data : [...c.items, ...res.data],
            total: res.total,
            page: res.page,
            lastPage: res.lastPage,
            loading: false,
            error: false,
        }));
    }

    useEffect(() => {
        commentsRequest.current += 1;
        setComments(EMPTY_COMMENTS);
        if (open && postId) loadComments(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, postId]);

    function handleLoadMoreComments() {
        if (comments.loading || comments.page >= comments.lastPage) return;
        loadComments(comments.page + 1);
    }

    async function handleSendComment(text) {
        const trimmed = String(text || "").trim();
        if (!post || sending || !trimmed || trimmed.length > COMMENT_MAX_LENGTH) {
            return false;
        }

        setSending(true);
        const result = await commentPost(post.id, trimmed);
        setSending(false);

        if (!result) {
            window.setSnackbar(t("postModal.comments.error"));
            return false;
        }

        // Newest first, like the API lists them.
        const comment = {
            id: result.comment_id,
            payload: { text: trimmed },
            publisher: me,
            timestamps: { created: new Date().toISOString() },
        };
        setComments((c) => ({
            ...c,
            items: [comment, ...c.items],
            total: c.total + 1,
        }));
        postStore.set(withCommentCount(post, 1));

        window.setSnackbar(t("postModal.comments.published"));
        return true;
    }

    // ── Overlay behavior ──────────────────────────────────────────────────
    // The page behind stops scrolling while open; ESC closes, but only while
    // this is the topmost modal (the admin options open on top of it).
    useEffect(() => {
        if (!open) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [open]);

    useEffect(() => {
        if (!open || !isTop) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, isTop, onClose]);

    // ── Actions ───────────────────────────────────────────────────────────
    const me = {
        id: user?.id,
        username: user?.username,
        nickname: user?.nickname,
        avatar: user?.avatar,
    };

    const shareLink = post
        ? postShareLink(communitySlugFor(post, (id) => community.get(id)), post.id)
        : "";

    const isAdmin = !!community.get(post?.community?.id)?.isAdmin?.();

    async function handleReact() {
        if (!post || reacting) return;
        setReacting(true);
        const done = await heartPost(post);
        if (!done) window.setSnackbar(t("community:feed.reactError"));
        setReacting(false);
    }

    function handleComment() {
        composerRef.current?.querySelector("textarea, input")?.focus();
    }

    function handleShare() {
        if (!post) return;
        window.handleOpenShareContentModal?.({
            title: t("community:feed.shareTitle"),
            link: shareLink,
        });
    }

    async function handleCopyLink() {
        if (!shareLink) return;
        try {
            await navigator.clipboard.writeText(shareLink);
            window.setSnackbar(t("postModal.linkCopied"));
        } catch (e) {
            window.setSnackbar(t("postModal.copyError"));
        }
    }

    // Points / deletion happen in the admin modal; afterwards the post is
    // re-read so the counts follow, or dropped from the store (which closes
    // this modal and removes the feed card) when it no longer exists.
    function handleOpenAdminOptions() {
        if (!post) return;
        adminModalRef.current?.open(post, async () => {
            const fresh = await fetchPost(post.id);
            if (fresh) postStore.set(fresh);
            else postStore.remove(post.id);
        });
    }

    return {
        zIndex,
        post,
        notFound,
        loading: !post && !notFound,
        comments,
        me,
        isAdmin,
        sending,
        reacting,
        adminModalRef,
        composerRef,
        handleReact,
        handleComment,
        handleShare,
        handleCopyLink,
        handleOpenAdminOptions,
        handleSendComment,
        handleLoadMoreComments,
    };
}
